import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getApiUrl } from "../utils/api";
import DynamicSidebar from "../components/DynamicSidebar";
import ProductCard from "../components/ProductCard";
import ComboSideMenu from "../components/ComboSideMenu";
import BulkSubcategoryModal from "../components/BulkSubcategoryModal";
import { useLocation, useNavigate } from "react-router-dom";

const Mainproducts = () => {
  const [products, setProducts] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [bulkCombos, setBulkCombos] = useState([]);
  const [selectedBulkCombo, setSelectedBulkCombo] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [activeBulkSubcategory, setActiveBulkSubcategory] = useState(null);

  const [comboItems, setComboItems] = useState([]);
  const [sideOpen, setSideOpen] = useState(false);

  const [selectedSize, setSelectedSize] = useState("");

  /* ✅ PAGINATION STATES (ADDED) */
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 12;

  const sizeOptions = ["S", "M", "L", "XL", "XXL", "3XL", "4XL"];

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const subId = location.state?.subcategoryId;
    if (subId) {
      setSelectedSubcategory(subId);
      setPage(1);
      fetchProducts(subId, 1);
    }
  }, []);

  const comboType = location.state?.comboType || null;
  const isBulk = comboType === "BULK";

  useEffect(() => {
    if (isBulk) fetchBulkCombos();
  }, [isBulk]);

  const fetchBulkCombos = async () => {
    try {
      const res = await fetch(getApiUrl("bulk-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) {
        setBulkCombos(data.data);
        setShowBulkModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveItem = (id) => {
    setComboItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleBulkSelect = (combo) => {
    setSelectedBulkCombo(combo);
    setActiveBulkSubcategory(combo.subcategoryId);
    setShowBulkModal(false);
    setPage(1);
    fetchProducts(combo.subcategoryId, 1);
  };

  /* ✅ FIXED: PRODUCTS APPEND WITH PAGINATION */
  const fetchProducts = async (subcategoryId, pageNo = 1) => {
    setSelectedSubcategory(subcategoryId);
    setSelectedSize("");

    try {
      const res = await fetch(getApiUrl("products/by-subcategory"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subcategoryid: subcategoryId,
          page: pageNo,
          limit: LIMIT,
        }),
      });

      const data = await res.json();
      if (data.success) {
        const normalized = data.data.map((p) => ({
          ...p,
          id: p._id,
        }));

        if (pageNo === 1) {
          setProducts(normalized);
        } else {
          setProducts((prev) => [...prev, ...normalized]);
        }

        setHasMore(normalized.length === LIMIT);
      }
    } catch {
      toast.error("Failed to load products");
    }
  };

  const handleComboSelect = (item) => {
    setComboItems((prev) => {
      if (isBulk) return [...prev, item];
      if (prev.some((p) => p.id === item.id)) {
        toast.warning("Product already selected");
        return prev;
      }
      if (
        comboType !== "MIXED" &&
        prev.some((p) => p.subcategoryId === item.subcategoryId)
      ) {
        toast.warning("One product per category only");
        return prev;
      }
      return [...prev, item];
    });

    setSideOpen(true);
  };

  const handleAddToCart = async (product) => {
    if (!selectedSize) return toast.warning("Select size");

    const userId = localStorage.getItem("userId");

    const res = await fetch(getApiUrl("cart-add"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        productId: product.id,
        selectedSize,
        quantity: 1,
      }),
    });

    const data = await res.json();
    if (data.success) navigate("/bskian-cart");
  };

  const handleAddWishlist = async (product) => {
    try {
      let userId = localStorage.getItem("userId");
      if (!userId) {
        userId = "guest_" + Date.now();
        localStorage.setItem("userId", userId);
      }

      const res = await fetch(getApiUrl("wishlist-add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          productId: product.id,
        }),
      });

      const data = await res.json();

      if (data.success && data.message === "Already in wishlist") {
        toast.info("Already in wishlist 💙");
      } else if (data.success) {
        toast.success("Added to Wishlist ❤️");
      } else {
        toast.error(data.message || "Failed");
      }
    } catch {
      toast.error("Something went wrong ❗");
    }
  };

  const handleCheckout = (orderData) => {
    navigate("/bsk-checkout", {
      state: {
        products: orderData.items,
        totalPrice: orderData.totalPrice,
        discount: orderData.totalDiscount,
        finalPrice: orderData.finalPrice,
        comboType: orderData.comboType,
      },
    });
  };

  const filteredProducts = selectedSize
    ? products.filter((p) => (p.sizes?.[selectedSize] || 0) > 0)
    : products;

  const handleBulkClose = () => {
    setShowBulkModal(false);
    setSelectedBulkCombo(null);
    setActiveBulkSubcategory(null);
    navigate("/bsk-main-product", { replace: true });
  };

  return (
    <div className="container-fluid">
      <BulkSubcategoryModal
        show={showBulkModal}
        combos={bulkCombos}
        onSelect={handleBulkSelect}
        onClose={handleBulkClose}
      />

      <div className="row">
        <div className="col-md-3 col-lg-2 p-0">
          <DynamicSidebar
            onSubCategorySelect={(id) => {
              setPage(1);
              fetchProducts(id, 1);
            }}
            disabledSubcategory={isBulk ? activeBulkSubcategory : null}
            initialSubcategory={location.state?.subcategoryId}
          />
        </div>

        <div className="col-md-9 col-lg-10 p-4">
          <div className="row">
            {filteredProducts.map((product) => (
              <div key={product.id} className="col-6 col-lg-3 mb-4">
                <ProductCard
                  product={product}
                  comboType={comboType}
                  onComboSelect={handleComboSelect}
                  onAddCart={() => handleAddToCart(product)}
                  onAddWishlist={() => handleAddWishlist(product)}
                />
              </div>
            ))}
          </div>

          {/* ✅ LOAD MORE BUTTON */}
          {hasMore && products.length > 0 && (
            <div className="text-center my-4">
              <button
                className="btn btn-outline-dark px-5"
                onClick={() => {
                  const nextPage = page + 1;
                  setPage(nextPage);
                  fetchProducts(selectedSubcategory, nextPage);
                }}
              >
                Load More
              </button>
            </div>
          )}

          {(comboType === "MIXED" || comboType === "BULK") && (
            <ComboSideMenu
              open={sideOpen}
              onClose={() => setSideOpen(false)}
              items={comboItems}
              onRemove={handleRemoveItem}
              comboType={comboType}
              onCheckout={handleCheckout}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Mainproducts;
